import {
  addDays,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInWeeks,
  endOfWeek,
  format,
  isSameYear,
  isValid,
  parse,
  parseISO,
  startOfWeek,
  subMonths,
  subYears,
} from 'date-fns'
import slugify from 'slugify'

import { PeriodPresets } from './dateRange'
import { formatDate, parseISODate } from './format'
import { pluralize, pluralizeS } from './strings'

type forceMinDifferenceInDaysParamsType = {
  min: number
  startDate: string | Date
  endDate: string | Date
}

export interface getPeriodFromDatesType {
  fromDate?: Date | string | number
  toDate?: Date | string | number
}

export const getEndDateAddOneDay = (start: string, end: string) => {
  return end && start === end ? addDays(parseISO(end), 1) : end
}

export const getDateFormatted = (value?: string | Date, format?: string) => {
  return formatDate(value, format || 'EEE, MMM dd, yyyy')
}

export const getDateAndTimeFormatted = (value?: string | Date) => {
  return {
    date: formatDate(value, 'EEE, MMM dd, yy'),
    time: formatDate(value, 'hh:mm a'),
  }
}

export function getPeriodFromDates({ fromDate, toDate }: getPeriodFromDatesType) {
  const from = fromDate ? formatDate(fromDate) : ''
  const to = toDate ? formatDate(toDate) : ''

  const isFromValid = isValid(parseISODate(from))
  const isToValid = isValid(parseISODate(to))

  return `${isFromValid ? from : ''}${isFromValid && isToValid ? '_' : ''}${isToValid ? to : ''}`
}

export function forceMinDifferenceInDays({
  min,
  startDate,
  endDate,
}: forceMinDifferenceInDaysParamsType) {
  const parsedStart = parseISODate(startDate)
  const parsedEnd = parseISODate(endDate)
  const differenceCount = differenceInDays(parsedEnd, parsedStart)

  if (differenceCount >= min) {
    return { startDate: parsedStart, endDate: parsedEnd }
  }

  return {
    startDate: parsedStart,
    endDate: addDays(parsedEnd, min - differenceCount),
  }
}

export const dateTo12MonthsBefore = (date: string | Date) => {
  const parsedDate = parseISODate(date)

  return formatDate(subYears(parsedDate, 1))
}

export const dateTo1MonthBefore = (date: string | Date) => {
  const parsedDate = parseISODate(date)
  return formatDate(subMonths(parsedDate, 1))
}

export const daysAgoFromToday = (date: string | Date): string => {
  const today = Date.now()
  const daysAgo = differenceInDays(today, parseISODate(date))
  if (daysAgo === 0) {
    return 'Today'
  }
  return `${daysAgo} day${pluralizeS(daysAgo)} ago`
}

export const timeAgoFromToday = (date: string | Date): string => {
  const today = Date.now()

  const minutesAgo = differenceInMinutes(today, parseISODate(date))

  if (minutesAgo < 1) return `Just now`

  if (minutesAgo < 60) return `${minutesAgo} ${pluralize(minutesAgo, 'minutes', 'minute')} ago`

  const hoursAgo = differenceInHours(today, parseISODate(date))
  if (hoursAgo < 24) return `${hoursAgo} ${pluralize(hoursAgo, 'hours', 'hour')} ago`

  const daysAgo = differenceInDays(today, parseISODate(date))
  if (daysAgo <= 14) return `${daysAgo} ${pluralize(daysAgo, 'days', 'day')} ago`

  const weeksAgo = differenceInWeeks(today, parseISODate(date))
  if (weeksAgo <= 4) return `${weeksAgo} ${pluralize(weeksAgo, 'weeks', 'week')} ago`

  const monthsAgo = differenceInCalendarMonths(today, parseISODate(date))

  return `${monthsAgo} ${pluralize(monthsAgo, 'months', 'month')} ago`
}

export const formatDurationSeconds = (totalSeconds: number): string => {
  const absoluteSeconds = Math.max(0, Math.round(totalSeconds))
  const minutes = Math.floor(absoluteSeconds / 60)
  const seconds = absoluteSeconds % 60

  if (minutes === 0) {
    return `${seconds}s`
  }

  if (seconds === 0) {
    return `${minutes}m`
  }

  return `${minutes}m ${seconds}s`
}

export const formatSignedSecondsDelta = (valueSeconds: number): string => {
  const roundedSeconds = Math.round(valueSeconds)
  return `${roundedSeconds >= 0 ? '+' : ''}${roundedSeconds}s`
}

export const timeAfterFromToday = (date: string | Date): string => {
  const todayDate = new Date()
  const year = todayDate.getFullYear()
  const month = (todayDate.getMonth() + 1).toString().padStart(2, '0')
  const day = todayDate.getDate().toString().padStart(2, '0')
  const today = new Date(`${year}-${month}-${day}`)

  const _date = parseISODate(date)

  if (today === _date) return 'today'

  const minutesAfter = differenceInMinutes(_date, todayDate)
  if (minutesAfter < 60) return `in ${minutesAfter} ${pluralize(minutesAfter, 'minutes', 'minute')}`

  const hoursAfter = differenceInHours(_date, todayDate)
  if (hoursAfter < 24) return `in ${hoursAfter} ${pluralize(hoursAfter, 'hours', 'hour')}`

  const daysAfter = differenceInDays(_date, todayDate)
  if (daysAfter < 7) return `in ${daysAfter} ${pluralize(daysAfter, 'days', 'day')}`

  const weeksAfter = differenceInWeeks(_date, todayDate)
  if (weeksAfter < 5) return `in ${weeksAfter} ${pluralize(weeksAfter, 'weeks', 'week')}`

  const monthsAfter = differenceInCalendarMonths(_date, todayDate)
  return `in ${monthsAfter} ${pluralize(monthsAfter, 'months', 'month')}`
}

export const getListOfDates = (startDate: string, endDate: string) => {
  const differentBetweenDays =
    differenceInCalendarDays(parseISODate(endDate), parseISODate(startDate)) + 1
  const arraySize = new Array(differentBetweenDays).fill(null)
  return arraySize.map((_, index) => addDays(parseISODate(startDate), index))
}

export const getWeekDates = (week: string) => {
  const date = parse(week, ISO_YEAR_WEEK_FORMAT, new Date())
  return {
    start: startOfWeek(date, { weekStartsOn: 1 }),
    end: endOfWeek(date, { weekStartsOn: 1 }),
  }
}

export const isPeriodPreset = (slug: string): slug is PeriodPresets => {
  return Object.values(PeriodPresets)
    .map(preset => slugify(preset))
    .includes(slug as PeriodPresets)
}

export const createNext7Dates = (format?: string) => {
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(yesterday)
    date.setDate(yesterday.getDate() + i)
    return formatDate(date, format)
  })
}

export const formatISODate = (dateString: string) =>
  format(parse(dateString, SHORT_ISO_DATE_FORMAT, new Date()), UI_DATE_FORMAT_FULL_YEAR)

export const parseISODateTime = (dateTimeString: string) => {
  return parse(dateTimeString, ISO_DATE_TIME_FORMAT, new Date())
}

export const formatISODateTime = (dateTimeString: string) => {
  return format(parseISODate(dateTimeString), `${UI_DATE_FORMAT_FULL_YEAR} ${UI_TIME_FORMAT}`)
}

export const formatToQuarter = (dateString: string, pattern = QUARTER_FORMAT) =>
  format(parse(dateString, SHORT_ISO_DATE_FORMAT, new Date()), pattern)

export const UI_DEFAULT_DATE_FORMAT = 'MMM d, yy'
export const SHORT_ISO_DATE_FORMAT = 'yyyy-MM-dd'
export const ISO_MONTH_FORMAT = 'yyyy-MM'
export const FULL_MONTH_NAME_FORMAT = 'MMMM'
export const SHORT_MONTH_NAME_FORMAT = 'MMM'
export const YEAR_FORMAT = 'yyyy'
export const SHORT_YEAR_FORMAT = 'yy'
export const ISO_YEAR_WEEK_FORMAT = "RRRR-'W'II" // 2024-W04
export const UI_DATE_FORMAT_FULL_YEAR = 'MMM d, yyyy'
export const UI_TIME_FORMAT = 'h:mm a'
export const ISO_DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm:ss' //2025-02-14 14:46:06
export const QUARTER_FORMAT = 'QQQ - yyyy'
export const MONTH_YEAR_FORMAT = 'MMMM yyyy'

export type MonthIsoString = string // format: yyyy-MM

export const parseMonthISOString = (monthString: MonthIsoString) =>
  parse(monthString, ISO_MONTH_FORMAT, new Date())

export const formatMonthISOStringRange = (from: MonthIsoString, to: MonthIsoString) => {
  const fromDate = parseMonthISOString(from)
  const toDate = parseMonthISOString(to)
  if (from === to) {
    return format(fromDate, `${SHORT_MONTH_NAME_FORMAT} ${YEAR_FORMAT}`)
  }
  if (isSameYear(fromDate, toDate)) {
    return `${format(fromDate, SHORT_MONTH_NAME_FORMAT)} - ${format(toDate, SHORT_MONTH_NAME_FORMAT)} ${format(toDate, YEAR_FORMAT)}`
  }
  return `${format(fromDate, SHORT_MONTH_NAME_FORMAT)} '${format(fromDate, SHORT_YEAR_FORMAT)} - ${format(toDate, SHORT_MONTH_NAME_FORMAT)} '${format(toDate, SHORT_YEAR_FORMAT)}`
}
